import {dataLinksApi, messagesService, templatesApi} from "@dv5/api"
import {
    clipboardService,
    CloseButton,
    ConfirmationDialog,
    DataLinkDialog,
    ErrorDisplayProcessor,
    MenuPosition,
} from "@dv5/components"
import {
    cancelEditWidget,
    CanvasWidgetsTemplateData,
    DataLink,
    DvDocument,
    getLayoutContents,
    getSelectedLayout,
    getWidgetComments,
    ItemAction,
    LayoutContentType,
    assignTagsToWidgets,
    moveWidgetsToLayers,
    Template, TemplateItemParams,
    TemplateType,
    useAppDispatch,
    useAppSelector,
    WidgetType,
} from "@dv5/domain"
import {CSSProperties, FC, MouseEvent, RefObject, useCallback, useEffect, useState} from "react"
import {isWidgetFullEditorTarget, WidgetFullEditorHostContext} from "../components/common/WidgetFullEditorPortal"
import {LinkLayer} from "../components/LinkLayer"
import {SelectionRectOverlay} from "../components/SelectionRectOverlay"
import {WidgetLinkToolbar} from "../components/widget-link/WidgetLinkToolbar"
import {CanvasMode} from "../model/CanvasMode"
import {CanvasPosition} from "../model/CanvasPosition"
import {canvasContentService} from "../services/canvasContentService"
import {CommentService} from "../services/commentService"
import {dataLinkService} from "../services/dataLinkService"
import {pocketService} from "@dv5/pocket"
import {useCanvasContent} from "./hooks/useCanvasContent"
import {useCanvasKeyboardHandlers} from "./hooks/useCanvasKeyboardHandlers"
import {useCanvasMouseHandlers} from "./hooks/useCanvasMouseHandlers"
import {getCanvasScale} from './utils/canvasViewport'
import {useCanvasSettings} from "./hooks/useCanvasSettings"
import {CanvasActionDialogs} from "./CanvasActionDialogs"
import {CanvasPopups} from "./CanvasPopups"
import {DocumentPicker} from '../components/document-widget/DocumentPicker'
import {TaskPicker} from '../components/task-widget/TaskPicker'
import {roundToGrid} from '../services/gridUtils'
import {menuItems, READ_ONLY_CANVAS_ACTIONS} from "./menuItems"
import {Widgets} from "./Widgets"
import './CanvasContent.less'

export interface Props {
    mode: CanvasMode
    canvasId: string
    zoom: number
    layerIds: Array<string>
    viewportRef: RefObject<HTMLDivElement | null>
    scrollPosition: CanvasPosition
    isPanning: boolean
    onChangeSelection: (selection: Array<string>) => void
    onStartEdit: (widgetId: string) => void
    onChangeTool: (newTool: CanvasMode) => void
    onNavigate: (dataLink: DataLink) => void
    onNavigateToDocument: (documentId: string, layoutId: string) => void
    onNavigateToTask: (taskId: string, layoutId: string) => void
}

export const CanvasContent: FC<Props> = ({
                                             mode,
                                             canvasId,
                                             zoom,
                                             layerIds,
                                             viewportRef,
                                             scrollPosition,
                                             isPanning,
                                             onChangeSelection,
                                             onStartEdit,
                                             onChangeTool,
                                             onNavigate,
                                             onNavigateToDocument,
                                             onNavigateToTask,
                                         }) => {
    const [fullEditorHost, setFullEditorHost] = useState<HTMLDivElement | null>(null)
    const {canvasContent, canvasSelection, editingWidgetId} = useCanvasContent(canvasId)
    const settings = useCanvasSettings(canvasId)
    const [showMenu, setShowMenu] = useState(false)
    const [menuPosition, setMenuPosition] = useState<MenuPosition>({x: 0, y: 0})
    const [clickedWidgetId, setClickedWidgetId] = useState<string | null>(null)
    const [showAddCommentDialog, setShowAddCommentDialog] = useState(false)
    const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false)
    const [showUseTemplateDialog, setShowUseTemplateDialog] = useState(false)
    const [templateClickPosition, setTemplateClickPosition] = useState<CanvasPosition | null>(null)
    const [documentClick, setDocumentClick] = useState<{position: CanvasPosition, ctrlKey: boolean} | null>(null)
    const [taskClick, setTaskClick] = useState<{position: CanvasPosition, ctrlKey: boolean} | null>(null)
    const [showAlignBar, setShowAlignBar] = useState(false)
    const [showMoveWidgetsToLayersDialog, setShowMoveWidgetsToLayersDialog] = useState(false)
    const [showAssignWidgetsToTagsDialog, setShowAssignWidgetsToTagsDialog] = useState(false)
    const [showDataLinkDialog, setShowDataLinkDialog] = useState(false)
    const [dataLinkWidgetId, setDataLinkWidgetId] = useState<string | null>(null)
    const [widgetsToDelete, setWidgetsToDelete] = useState<string[] | null>(null)

    const dispatch = useAppDispatch()

    useEffect(() => {
        setShowAlignBar(canvasSelection.length >= 2)
    }, [canvasSelection])
    const widgetComments = useAppSelector(getWidgetComments)
    const selectedLayout = useAppSelector(getSelectedLayout)
    const layoutContents = useAppSelector(getLayoutContents)
    const currentLayoutContent = selectedLayout?.id ? layoutContents[selectedLayout.id] : undefined
    const additionalId = (currentLayoutContent?.content as any)?.id ?? ''
    const additionalType = currentLayoutContent?.type ?? LayoutContentType.VIEW
    const affectedWidgets = canvasSelection.length > 0
        ? canvasContent.widgets.filter(widget => canvasSelection.includes(widget.id))
        : (clickedWidgetId ? canvasContent.widgets.filter(widget => widget.id === clickedWidgetId) : [])

    const stopEditing = () => {
        if (editingWidgetId) {
            dispatch(cancelEditWidget({
                canvasId: canvasId,
                changes: editingWidgetId,
            }))
        }
    }

    const handleLinkClick = (linkId: string, ctrlKey: boolean) => {
        if (ctrlKey) {
            const newSelection = dragState.selectedLinkIds.includes(linkId)
                ? dragState.selectedLinkIds.filter(id => id !== linkId)
                : [...dragState.selectedLinkIds, linkId]
            setDragState({...dragState, selectedLinkIds: newSelection})
        } else {
            onChangeSelection([])
            setDragState({...dragState, selectedLinkIds: [linkId]})
        }
    }
    const handleSaveCoordinates = useCallback((canvasPosition: CanvasPosition) => {
        if (showUseTemplateDialog) {
            return
        }
        setTemplateClickPosition(canvasPosition)
        setShowUseTemplateDialog(true)
    }, [showUseTemplateDialog])

    const {
        dragState,
        setDragState,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        startDrag,
        startResize,
        startLinkCreation,
        completeLinkCreation,
    } = useCanvasMouseHandlers({
        mode,
        canvasId,
        zoom,
        scrollPosition,
        layerIds,
        showUseTemplateDialog,
        canvasContent,
        canvasSelection,
        onChangeSelection,
        onStartEdit,
        onChangeTool,
        canvasRef: viewportRef,
        onSaveCoordinates: handleSaveCoordinates,
        onChooseDocument: (position, ctrlKey) => setDocumentClick({position, ctrlKey}),
        onChooseTask: (position, ctrlKey) => setTaskClick({position, ctrlKey}),
    })

    useEffect(() => {
        if (!settings.readOnly) return
        setShowUseTemplateDialog(false)
        setTemplateClickPosition(null)
        setDocumentClick(null)
        setTaskClick(null)
        setShowMoveWidgetsToLayersDialog(false)
        setShowAssignWidgetsToTagsDialog(false)
        setWidgetsToDelete(null)
        onChangeTool('select')
    }, [settings.readOnly])

    const {handleKeyDown} = useCanvasKeyboardHandlers({
        canvasId,
        canvasContent,
        canvasSelection,
        onChangeSelection,
        editingWidgetId,
        dragState,
        setDragState,
        onRequestDelete: setWidgetsToDelete,
    })

    const handleContextMenu = (e: MouseEvent<HTMLDivElement>, widgetId: string) => {
        e.preventDefault()
        e.stopPropagation()
        const rect = viewportRef.current?.getBoundingClientRect()
        setMenuPosition({
            x: e.clientX - (rect?.left || 0),
            y: e.clientY - (rect?.top || 0)
        })
        setClickedWidgetId(widgetId)
        setShowMenu(true)
    }

    const handleHideMenu = () => {
        setShowMenu(false)
        setClickedWidgetId(null)
    }

    const handleMenuAction = (action: ItemAction) => {
        setShowMenu(false)

        const widgetsToProcess = canvasSelection.length > 0 ? canvasSelection : (clickedWidgetId ? [clickedWidgetId] : [])

        if (settings.readOnly && !READ_ONLY_CANVAS_ACTIONS.has(action)) return

        if (widgetsToProcess.length === 0) {
            return
        }

        if (action === ItemAction.DELETE) {
            setWidgetsToDelete(widgetsToProcess)
            return
        } else if (action === ItemAction.CLONE) {
            canvasContentService.duplicateWidgets(canvasId, widgetsToProcess, canvasContent.widgets, settings.alignToGrid)
        } else if (action === ItemAction.COPY) {
            const copyText = canvasContentService.copyWidgets(widgetsToProcess, canvasContent.widgets)
            clipboardService.copyTo(copyText, true)
        } else if (action === ItemAction.ADD_DATA_LINK) {
            if (widgetsToProcess.length !== 1) {
                setClickedWidgetId(null)
                return
            }
            const pocketItem = pocketService.getSelectedItem()
            if (pocketItem) {
                dataLinkService.createLink(widgetsToProcess, canvasId)
            } else {
                setDataLinkWidgetId(widgetsToProcess[0])
                setShowDataLinkDialog(true)
                return
            }
        } else if (action === ItemAction.ADD_COMMENT) {
            setShowAddCommentDialog(true)
            return
        } else if (action === ItemAction.MOVE_TO_LAYERS) {
            setShowMoveWidgetsToLayersDialog(true)
            return
        } else if (action === ItemAction.ASSIGN_TO_TAGS) {
            setShowAssignWidgetsToTagsDialog(true)
            return
        } else if (action === ItemAction.SAVE_AS_TEMPLATE) {
            setShowSaveTemplateDialog(true)
        } else if (action === ItemAction.TOGGLE_LOCKED) {
            for (const widgetId of widgetsToProcess) {
                messagesService.send('canvas.toggle-widget-locked', {
                    canvasId,
                    widgetId,
                })
            }
        }

        setClickedWidgetId(null)
    }

    const handleSaveTemplate = (title: string) => {
        const widgetsToProcess = canvasSelection.length > 0 ? canvasSelection : (clickedWidgetId ? [clickedWidgetId] : [])
        const templateData: CanvasWidgetsTemplateData = {
            sourceCanvasId: canvasId,
            sourceWidgetIds: widgetsToProcess,
        }
        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        templatesApi.createTemplate(title, TemplateType.CANVAS_WIDGETS, templateData, errorProcessor, () => {
            setShowSaveTemplateDialog(false)
            setClickedWidgetId(null)
        }).catch(console.error)
    }

    const handleCancelSaveTemplate = () => {
        setShowSaveTemplateDialog(false)
    }

    const handleMoveWidgetsToLayers = (layerIds: string[]) => {
        if (settings.readOnly) {
            return
        }
        const widgetsToProcess = canvasSelection.length > 0 ? canvasSelection : (clickedWidgetId ? [clickedWidgetId] : [])
        if (widgetsToProcess.length === 0) {
            return
        }
        const assignments = widgetsToProcess.map(widget => {
            return {
                widgetId: widget,
                layers: layerIds.join(',')
            }
        })
        messagesService.send('canvas.move-widgets-to-layers', {
            canvasId,
            assignments,
        })
        dispatch(moveWidgetsToLayers({canvasId, changes: assignments}))
        setShowMoveWidgetsToLayersDialog(false)
        setClickedWidgetId(null)
    }

    const handleAssignWidgetsToTags = (tagIds: string[]) => {
        if (settings.readOnly) return
        const widgetsToProcess = canvasSelection.length > 0 ? canvasSelection : (clickedWidgetId ? [clickedWidgetId] : [])
        if (widgetsToProcess.length === 0) return
        const assignments = widgetsToProcess.map(widget => {
            return {
                widgetId: widget,
                tags: tagIds.join(',')
            }
        })
        messagesService.send('canvas.assign-tags-to-widgets', {
            canvasId,
            assignments,
        })
        dispatch(assignTagsToWidgets({canvasId, changes: assignments}))
        setShowAssignWidgetsToTagsDialog(false)
        setClickedWidgetId(null)
    }

    const handleCanvasMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        if (!isWidgetFullEditorTarget(e.target)) handleMouseDown(e)
    }

    const handleTemplateSelect = (template: Template, params: TemplateItemParams) => {
        if (settings.readOnly) return
        if (templateClickPosition && viewportRef.current) {
            canvasContentService.createFromTemplate(canvasId, layerIds, templateClickPosition, template, params, settings.alignToGrid)
        }
        setShowUseTemplateDialog(false)
        setTemplateClickPosition(null)
        onChangeTool('select')
    }

    const handleCancelTemplate = () => {
        setShowUseTemplateDialog(false)
        setTemplateClickPosition(null)
    }

    const handleCloseCommentDialog = () => {
        setShowAddCommentDialog(false)
        setClickedWidgetId(null)
    }

    const handleCloseMoveWidgetsToLayersDialog = () => {
        setShowMoveWidgetsToLayersDialog(false)
        setClickedWidgetId(null)
    }

    const handleCloseAssignWidgetsToTagsDialog = () => {
        setShowAssignWidgetsToTagsDialog(false)
        setClickedWidgetId(null)
    }

    const handleCloseAlignBar = () => {
        setShowAlignBar(false)
    }

    const handleCloseDataLinkDialog = () => {
        setShowDataLinkDialog(false)
        setDataLinkWidgetId(null)
    }

    const handleConfirmDeleteWidgets = () => {
        if (widgetsToDelete && !settings.readOnly) {
            canvasContentService.deleteWidgets(canvasId, widgetsToDelete)
        }
        setWidgetsToDelete(null)
    }

    const handleCancelDeleteWidgets = () => {
        setWidgetsToDelete(null)
    }

    const handleDeleteDataLink = (dataLink: DataLink, sourceItemId: string) => {
        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        dataLinksApi.deleteDataLink(sourceItemId, dataLink.id, errorProcessor, () => {
            setShowDataLinkDialog(false)
            setDataLinkWidgetId(null)
        }).catch(console.error)
    }

    const scale = getCanvasScale(zoom)
    const styles: CSSProperties = settings.displayGrid ? {
        backgroundSize: `${8 * scale}px ${8 * scale}px`,
        backgroundPosition: `${-scrollPosition.left * scale}px ${-scrollPosition.top * scale}px`,
    } : {}
    const worldStyles: CSSProperties = {
        transform: `translate(${-scrollPosition.left * scale}px, ${-scrollPosition.top * scale}px) scale(${scale})`,
    }

    const getLinkToolbar = () => {
        if (settings.readOnly || dragState.selectedLinkIds.length !== 1) {
            return null
        }
        const selectedLink = canvasContent.widgetLinks.find(l => l.id === dragState.selectedLinkIds[0])
        if (!selectedLink) {
            return null
        }
        const startWidget = canvasContent.widgets.find(w => w.id === selectedLink.startWidgetId)
        const endWidget = canvasContent.widgets.find(w => w.id === selectedLink.endWidgetId)
        if (!startWidget || !endWidget) {
            return null
        }
        const midX = (startWidget.left + endWidget.left) / 2
        const midY = (startWidget.top + endWidget.top) / 2
        const toolbarStyles: CSSProperties = {
            left: `${midX}px`,
            top: `${midY - 80}px`,
            transform: 'translateX(-50%)',
        }
        return (
            <WidgetLinkToolbar
                canvasId={canvasId}
                linkId={selectedLink.id}
                startWidgetId={selectedLink.startWidgetId}
                endWidgetId={selectedLink.endWidgetId}
                data={selectedLink.data as any}
                toolbarStyles={toolbarStyles}
            />
        )
    }
    const commentService = new CommentService(canvasId, clickedWidgetId, () => {
        setShowAddCommentDialog(false)
        setClickedWidgetId(null)
    })

    return (
        <div
            className={`canvasContent${dragState.selectionRect ? ' selecting' : ''}${isPanning ? ' panning' : ''}${settings.displayGrid ? ' canvas-grid' : ''}`}
            style={styles}
            ref={viewportRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={isPanning ? undefined : handleMouseUp}
        >
            <WidgetFullEditorHostContext.Provider value={fullEditorHost}>
                <div className="canvasWorld" style={worldStyles}>
                    <div className="canvasMainArea">
                        <Widgets canvasId={canvasId}
                                 widgets={canvasContent.widgets}
                                 selection={canvasSelection}
                                 settings={settings}
                                 editingWidgetId={editingWidgetId}
                                 onStartDrag={startDrag}
                                 onStartResize={startResize}
                                 onContextMenu={handleContextMenu}
                                 dragState={dragState}
                                 onStartLinkCreation={startLinkCreation}
                                 onCompleteLinkCreation={completeLinkCreation}
                                 onNavigate={onNavigate}
                                 onNavigateToDocument={onNavigateToDocument}
                                 onNavigateToTask={onNavigateToTask}
                                 onStopEditing={stopEditing}
                        />
                        <LinkLayer
                            widgetLinks={canvasContent.widgetLinks}
                            widgets={canvasContent.widgets}
                            linkCreation={dragState.linkCreation}
                            mousePosition={dragState.mousePosition}
                            selectedLinkIds={dragState.selectedLinkIds}
                            onLinkClick={handleLinkClick}
                        />
                        {getLinkToolbar()}
                    </div>
                    <SelectionRectOverlay selectionRect={dragState.selectionRect}/>
                </div>
            </WidgetFullEditorHostContext.Provider>
            <div className="canvas-editor-overlay" ref={setFullEditorHost}/>
            {documentClick && <DocumentPicker
                onCancel={() => setDocumentClick(null)}
                onSelect={(document: DvDocument) => {
                    if (settings.readOnly) return
                    const {position, ctrlKey} = documentClick
                    messagesService.send('canvas.create-widget', {
                        canvasId,
                        type: WidgetType.DOCUMENT,
                        left: settings.alignToGrid ? roundToGrid(position.left) : position.left,
                        top: settings.alignToGrid ? roundToGrid(position.top) : position.top,
                        width: 400,
                        height: 300,
                        zIndex: 0,
                        data: {documentId: document.id, title: document.title},
                        layers: layerIds.join(','),
                        tags: '',
                    })
                    setDocumentClick(null)
                    if (!ctrlKey) onChangeTool('select')
                }}
            />}
            {taskClick && <TaskPicker
                onCancel={() => setTaskClick(null)}
                onSelect={(task) => {
                    if (settings.readOnly) return
                    const {position, ctrlKey} = taskClick
                    messagesService.send('canvas.create-widget', {
                        canvasId,
                        type: WidgetType.TASK,
                        left: settings.alignToGrid ? roundToGrid(position.left) : position.left,
                        top: settings.alignToGrid ? roundToGrid(position.top) : position.top,
                        width: 320,
                        height: 220,
                        zIndex: 0,
                        data: {taskId: task.id, viewId: task.viewId, title: task.title, code: task.code, status: task.status, description: task.description},
                        layers: layerIds.join(','),
                        tags: '',
                    })
                    setTaskClick(null)
                    if (!ctrlKey) onChangeTool('select')
                }}
            />}
            <CanvasActionDialogs
                showAddCommentDialog={showAddCommentDialog}
                clickedWidgetId={clickedWidgetId}
                widgetComments={widgetComments}
                commentService={commentService}
                onCloseCommentDialog={handleCloseCommentDialog}
                markdownComments={settings.markdownComments}
                showSaveTemplateDialog={showSaveTemplateDialog}
                onSaveTemplate={handleSaveTemplate}
                onCancelSaveTemplate={handleCancelSaveTemplate}
                showUseTemplateDialog={showUseTemplateDialog}
                onTemplateSelect={handleTemplateSelect}
                onCancelTemplate={handleCancelTemplate}
            />
            <CanvasPopups
                showMenu={showMenu}
                menuPosition={menuPosition}
                menuItems={menuItems.filter(item => !settings.readOnly || (item.action !== undefined && READ_ONLY_CANVAS_ACTIONS.has(item.action)))}
                onMenuItem={handleMenuAction}
                onHideMenu={handleHideMenu}
                showMoveWidgetsToLayersDialog={showMoveWidgetsToLayersDialog}
                onMoveWidgetsToLayers={handleMoveWidgetsToLayers}
                onCloseMoveWidgetsToLayersDialog={handleCloseMoveWidgetsToLayersDialog}
                additionalId={additionalId}
                additionalType={additionalType}
                showAssignWidgetsToTagsDialog={showAssignWidgetsToTagsDialog}
                onAssignWidgetsToTags={handleAssignWidgetsToTags}
                onCloseAssignWidgetsToTagsDialog={handleCloseAssignWidgetsToTagsDialog}
                showAlignBar={showAlignBar && !settings.readOnly}
                canvasId={canvasId}
                widgets={canvasContent.widgets}
                canvasSelection={canvasSelection}
                affectedWidgets={affectedWidgets}
                onCloseAlignBar={handleCloseAlignBar}
            />
            {showDataLinkDialog && dataLinkWidgetId && (
                <div className="data-link-dialog-overlay" onClick={(e) => e.target === e.currentTarget && handleCloseDataLinkDialog()}>
                    <div className="data-link-dialog">
                        <CloseButton onClose={handleCloseDataLinkDialog} className="data-link-dialog-close"/>
                        <DataLinkDialog sourceItemId={dataLinkWidgetId}
                                        dataLinkCount={0}
                                        onNavigate={onNavigate}
                                        onDelete={handleDeleteDataLink}
                                        quickCreation={true}
                        />
                    </div>
                </div>
            )}
            {widgetsToDelete && widgetsToDelete.length > 0 && (
                <ConfirmationDialog
                    message={widgetsToDelete.length === 1
                        ? 'Delete this widget? This action cannot be undone.'
                        : `Delete ${widgetsToDelete.length} widgets? This action cannot be undone.`}
                    onConfirm={handleConfirmDeleteWidgets}
                    onCancel={handleCancelDeleteWidgets}
                />
            )}
        </div>
    )
}
